import {
  ArrowUpRight01Icon,
  Clock01Icon,
  Link04Icon,
  RefreshIcon
} from '@hugeicons/react'
import { DateTime } from 'luxon'

import { AppIcon } from '@/shared/ui/icons'
import { Badge } from '@/shared/ui/Badge'
import { Button } from '@/shared/ui/Button'
import { RewardBadge } from '@/shared/ui/RewardBadge'
import type { MissionWithProgress } from '@/types/mission'

import {
  MISSION_SHOW_STATUS_BADGE,
  MISSION_SHOW_STATUS_HINTS,
  MISSION_SHOW_STATUS_LABELS,
  type MissionShowStatus
} from '../../constants/showStatus'

type MissionSubmittedEvidenceBlockProps = {
  mission: MissionWithProgress
  submission: NonNullable<MissionWithProgress['_progress']['submission']>
  showStatus: Extract<MissionShowStatus, 'pending' | 'approved' | 'refused'>
  canResubmit: boolean
  resubmitOpen: boolean
  onResubmit: () => void
  onCancelResubmit: () => void
}

const EvidenceContent = ({
  mission,
  submission
}: {
  mission: MissionWithProgress
  submission: NonNullable<MissionWithProgress['_progress']['submission']>
}) => {
  const kind = mission.evidence_kind

  if (kind === 'image' && submission.media?.file?.url) {
    return (
      <img
        src={submission.media.file.url}
        alt="Evidência enviada"
        className="max-h-56 w-full rounded-lg object-cover"
      />
    )
  }

  if (kind === 'file' && submission.media?.file?.url) {
    return (
      <a
        href={submission.media.file.url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex min-w-0 items-center gap-2 text-sm font-semibold text-primary no-underline"
      >
        <AppIcon name="file-alt" />
        <span className="truncate">Abrir arquivo enviado</span>
        <ArrowUpRight01Icon size={14} variant="stroke" aria-hidden />
      </a>
    )
  }

  if (kind === 'link' && submission.content) {
    const href = submission.content.startsWith('http')
      ? submission.content
      : `https://${submission.content}`

    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex min-w-0 items-start gap-2 break-all text-sm font-semibold text-primary no-underline"
      >
        <Link04Icon size={16} variant="duotone" className="mt-0.5 shrink-0" aria-hidden />
        <span>{submission.content}</span>
        <ArrowUpRight01Icon size={14} variant="stroke" className="mt-0.5 shrink-0" aria-hidden />
      </a>
    )
  }

  return (
    <p className="m-0 whitespace-pre-wrap text-sm leading-relaxed text-gray-800">
      {submission.content || '—'}
    </p>
  )
}

export const MissionSubmittedEvidenceBlock = ({
  mission,
  submission,
  showStatus,
  canResubmit,
  resubmitOpen,
  onResubmit,
  onCancelResubmit
}: MissionSubmittedEvidenceBlockProps) => {
  const submittedAt = submission.created_at
    ? DateTime.fromISO(submission.created_at).toFormat("dd/MM 'às' HH:mm")
    : null

  if (resubmitOpen) {
    return (
      <div className="flex items-center justify-between gap-3 border-t border-contour pt-4">
        <p className="m-0 text-sm font-semibold text-gray-800">
          Enviando nova evidência
        </p>
        <button
          type="button"
          onClick={onCancelResubmit}
          className="shrink-0 border-0 bg-transparent p-0 text-sm font-semibold text-gray-700 underline"
        >
          Cancelar
        </button>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4 border-t border-contour pt-4">
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <Badge
            variant={MISSION_SHOW_STATUS_BADGE[showStatus]}
            size="md"
            tone="light"
            showIcon
          >
            {MISSION_SHOW_STATUS_LABELS[showStatus]}
          </Badge>
          {submittedAt && (
            <span className="inline-flex items-center gap-1 text-2xs font-semibold text-gray-600">
              <Clock01Icon size={12} variant="stroke" aria-hidden />
              {submittedAt}
            </span>
          )}
        </div>
        <p className="m-0 text-sm text-gray-700">
          {MISSION_SHOW_STATUS_HINTS[showStatus]}
        </p>
      </div>

      <div className="flex flex-col gap-1.5">
        <span className="text-caps font-bold uppercase tracking-wide text-gray-700">
          O que você enviou
        </span>
        <EvidenceContent mission={mission} submission={submission} />
      </div>

      {showStatus === 'approved' && (
        <RewardBadge.Group>
          {(mission.point_reward ?? 0) > 0 && (
            <RewardBadge
              type="xp"
              value={`+${mission.point_reward}`}
              variant="inline"
            />
          )}
          {(mission.coin_reward ?? 0) > 0 && (
            <RewardBadge
              type="coins"
              value={`+${mission.coin_reward}`}
              variant="inline"
            />
          )}
        </RewardBadge.Group>
      )}

      {showStatus === 'refused' && submission.rejection_feedback && (
        <div className="border-l-4 border-red-500 pl-3">
          <span className="text-caps font-bold uppercase tracking-wide text-red-700">
            O que precisa corrigir
          </span>
          <p className="m-0 mt-1 text-sm leading-relaxed text-red-800">
            {submission.rejection_feedback}
          </p>
        </div>
      )}

      {showStatus === 'pending' && canResubmit && (
        <Button
          type="button"
          variant="primary-ghost"
          size="md"
          className="w-full justify-center"
          onClick={onResubmit}
          leadingIcon={<RefreshIcon size={16} variant="stroke" />}
          showTrailingIcon={false}
        >
          Enviar nova evidência
        </Button>
      )}
    </div>
  )
}
